import { intlLocale, translate } from '../i18n/index.ts';

/** Presentation helpers shared across views. */

/** '@'-prefixed handle, as the app displays usernames everywhere. */
export function handle(username: string): string {
  return `@${username}`;
}

/** Regional-indicator flag emoji for an ISO 3166-1 alpha-2 code. */
export function countryFlag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65),
  );
}

/** Up to two initials, used as the avatar fallback. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact age for lists: "now", "5m", "3h", "2d", then a date. */
export function shortTimeAgo(iso: string, now: number = Date.now()): string {
  const elapsed = now - new Date(iso).getTime();
  if (Number.isNaN(elapsed)) return '';

  const locale = intlLocale.value;
  // `Intl.RelativeTimeFormat` rather than "5m" glued together: every language
  // words this differently, and several put the number after the unit. The
  // browser already knows all of it, in far more languages than this app has
  // catalogues for.
  if (elapsed < MINUTE) return translate('common.justNow');
  if (elapsed < HOUR) return relative(-Math.floor(elapsed / MINUTE), 'minute', locale);
  if (elapsed < DAY) return relative(-Math.floor(elapsed / HOUR), 'hour', locale);
  if (elapsed < 7 * DAY) return relative(-Math.floor(elapsed / DAY), 'day', locale);

  return new Date(iso).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

function relative(value: number, unit: Intl.RelativeTimeFormatUnit, locale: string): string {
  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' }).format(
      value,
      unit,
    );
  } catch {
    return `${Math.abs(value)}${unit.charAt(0)}`;
  }
}

/** Clock time for a message bubble. */
export function messageTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(intlLocale.value, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Day heading used to separate runs of messages. */
export function messageDay(iso: string, now: number = Date.now()): string {
  const date = new Date(iso);
  const today = new Date(now);
  const isSameDay = date.toDateString() === today.toDateString();
  // `numeric: 'auto'` is what turns "in 0 days" into "today" in every
  // language, rather than us hard-coding the two words in English.
  if (isSameDay) return relative(0, 'day', intlLocale.value);

  const yesterday = new Date(now - DAY);
  if (date.toDateString() === yesterday.toDateString()) {
    return relative(-1, 'day', intlLocale.value);
  }

  return date.toLocaleDateString(intlLocale.value, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

/** Absolute timestamp for a tooltip. */
export function fullTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(intlLocale.value);
}

/**
 * A price in minor units, as somebody reads it.
 *
 * `Intl` owns the symbol, the separators and which side the symbol goes on —
 * none of which is the same in Thai and English, and all of which a
 * hand-rolled formatter gets wrong for one of them.
 *
 * Lives here rather than in the card that first needed it: the chat header
 * shows the price of the item a conversation is about, and a second copy of
 * this is a second place for ฿25,000 to become 25000฿.
 */
export function money(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(intlLocale.value, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(minor / 100);
  } catch {
    // An unknown currency code throws rather than falling back, and no price
    // at all is worse than a bare number.
    return String(Math.round(minor / 100));
  }
}
